import { Box, Stack, Typography } from '@mui/material'

import {
  Artist,
  ConsoleCampaignsConnectionFragment,
  Maybe,
  MembershipSubscriptionProduct,
} from '@/clients/utoniq-core/schema'
import {
  ArtistHeaderItemsLeft,
  ArtistHeaderItemsLeftProps,
  ArtistHeaderItemsRight,
  Head,
} from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { CampaignBoxSkeleton, ConsoleCampaignBox } from '@/components/organisms'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  artistId: string
  currentUser: Maybe<ArtistHeaderItemsLeftProps['currentUser']>
  loading: boolean
  artist: Maybe<
    Pick<Artist, 'id' | 'paymentType' | 'hasArrangingDeliveryItems'>
  >
  hasNextPage: boolean
  campaigns: Maybe<ConsoleCampaignsConnectionFragment>
  membership: Pick<MembershipSubscriptionProduct, 'productId'>[] | undefined
}

export type Action = {
  onFetchMore: () => Promise<void>
}

export const ConsoleCampaignsTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
}) => {
  const { routerNames } = useRouter()
  const { t } = useTranslation('consoleCampaigns')

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleCampaigns} />

      <ConsoleBaseLayout
        header={{
          left: (
            <ArtistHeaderItemsLeft
              currentUser={state.currentUser}
              paymentType={state.artist?.paymentType}
              hasArrangingDeliveryItems={
                state.artist?.hasArrangingDeliveryItems
              }
              membership={state.membership}
            />
          ),
          right: <ArtistHeaderItemsRight artistId={state.artistId} />,
        }}
      >
        <Box sx={{ py: 5, px: 5 }}>
          <Typography variant="h3">{t('title')}</Typography>
        </Box>
        <ScrollFetcher
          loading={state.loading}
          onFetchMore={onFetchMore}
          hasNextPage={state.hasNextPage}
          loadingComponent={
            <Stack spacing={6} sx={{ py: 4, px: 5 }}>
              {[1, 2, 3, 4, 5].map(i => (
                <CampaignBoxSkeleton key={i} />
              ))}
            </Stack>
          }
          nodes={state.campaigns?.edges.map(({ node }) => (
            <ConsoleCampaignBox key={node.id} campaign={node} />
          ))}
          emptyMessage={t('missionEmptyMessage')}
        />
        <Box sx={{ mb: 3 }} />
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}
